import { Input } from '@/components/ui/input';
import type { LaborSubItemForm } from './subItemTypes';
import { getSubItemValue, getUnitLabel } from '@jobos/shared';
import { SubItemRowWrapper } from './SubItemRowWrapper';
import { LaborRateSelect } from './LaborRateSelect';
import { formatCurrency } from '@/lib/utils';
import { MarkupRateBadge } from './MarkupRateBadge';
import { UnitLabelInput } from '@/components/shared/UnitLabelInput';
import { useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';

interface LaborSubItemRowProps {
  item: LaborSubItemForm;
  isEditingNote: boolean;
  onUpdate: (updates: Partial<LaborSubItemForm>) => void;
  onDelete: () => void;
  onStartEditingNote: () => void;
  onStopEditingNote: () => void;
  readOnly?: boolean;
  jobMarkupRate: number;
  label?: string;
  category?: string;
  isSelected?: boolean;
  onClick?: () => void;
}

export function LaborSubItemRow({
  item,
  isEditingNote,
  onUpdate,
  onDelete,
  onStartEditingNote,
  onStopEditingNote,
  readOnly = false,
  jobMarkupRate,
  label = 'Labor',
  category = 'labor',
  isSelected = false,
  onClick,
}: LaborSubItemRowProps) {
  const typeMeta = useSubItemTypeMeta();
  const abbr = getUnitLabel({
    unitKey: item.unitKey,
    categoryUnitLabel: typeMeta[category]?.unitLabel ?? null,
    rateType: item.rateType,
  });

  return (
    <SubItemRowWrapper
      label={label}
      note={item.note}
      isEditingNote={isEditingNote}
      onNoteChange={(note) => onUpdate({ note })}
      onStartEditingNote={onStartEditingNote}
      onStopEditingNote={onStopEditingNote}
      onDelete={onDelete}
      readOnly={readOnly}
      isSelected={isSelected}
      onClick={onClick}
    >
      <div className="flex flex-col items-end gap-0.5 pl-2">
        <div className="flex flex-row items-center gap-1.5">
          <Input
            type="number"
            min="0"
            step="0.5"
            value={item.hours || ''}
            onChange={(e) => onUpdate({ hours: parseFloat(e.target.value) || 0 })}
            placeholder="Qty"
            className="h-7 w-14 px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            disabled={readOnly}
          />
          <div className="w-20" onClick={(e) => e.stopPropagation()}>
            <UnitLabelInput
              value={item.unitKey ?? ''}
              onChange={(v) => onUpdate({ unitKey: v || undefined })}
              placeholder={abbr}
              disabled={readOnly}
              className="h-7 px-2 text-xs"
            />
          </div>
          <span className="text-xs text-gray-400">&times;</span>
          <div className="w-36" onClick={(e) => e.stopPropagation()}>
            <LaborRateSelect
              value={item.rate}
              unitKey={item.unitKey}
              onChange={(rate, unitKey) => onUpdate({ rate, ...(unitKey !== undefined && { unitKey }) })}
              disabled={readOnly}
              category={category}
            />
          </div>
          <span className="text-xs text-gray-400">=</span>
          <span className="text-sm font-medium text-gray-700 tabular-nums">
            {formatCurrency(getSubItemValue(item, typeMeta))}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-400">Markup:</span>
          <MarkupRateBadge
            markupRate={item.markupRate}
            jobMarkupRate={jobMarkupRate}
            onChange={(markupRate) => onUpdate({ markupRate })}
            readOnly={readOnly}
          />
        </div>
      </div>
    </SubItemRowWrapper>
  );
}
